import { ReactNode, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import _get from "lodash/get";

import DocRegularIcon from "@assets/icons/file-doc-regular.svg";

import ToastService from "@services/toast.service";
import { APP_COLORS } from "@styles/colors";
import { DEAL_DOC_ACCEPT_FORMATS, MAX_FILE_SIZE_MB } from "@config/constants";
import { UPLOAD_ERROR_MESSAGES } from "@config/messages";
import { formatsToLabel } from "@utils/files.util";

interface IUploaderDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  dropTitle?: ReactNode;
}

export function UploaderDropzone({ onFilesSelected, dropTitle }: IUploaderDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const handleFileDrop = useCallback(
    (items: DataTransferItemList | undefined) => {
      if (items) {
        const validFiles: File[] = [];
        const folders: File[] = [];

        for (let i = 0; i < items?.length; i++) {
          const item = items[i].webkitGetAsEntry();
          if (item && item.isDirectory) {
            folders.push(items[i].getAsFile() as File);
          } else if (item && items[i].getAsFile()) {
            validFiles.push(items[i].getAsFile() as File);
          }
        }

        if (_get(folders, "length") > 0) {
          ToastService.showError(UPLOAD_ERROR_MESSAGES.FOLDERS);
        }

        if (_get(validFiles, "length") > 0) {
          const fileList = new DataTransfer();
          validFiles.forEach((file) => fileList.items.add(file));
          onFilesSelected(Array.from(fileList.files || []));
        }
      }
    },
    [onFilesSelected]
  );

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((c) => c + 1);
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((c) => {
        const newCount = c - 1;
        if (newCount <= 0) {
          setIsDragging(false);
          return 0;
        }
        return newCount;
      });
    };

    const handleDrop = (e: DragEvent) => {
      e.dataTransfer?.items;
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(0);
      setIsDragging(false);
      handleFileDrop(e.dataTransfer?.items);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("dragover", handleDragOver);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("dragover", handleDragOver);
    };
  }, [onFilesSelected]);

  if (!isDragging) return null;

  return createPortal(
    <div className="p-3 fixed inset-0 z-[9999] flex items-center justify-center bg-gray-100/70 dark:bg-gray-700/70">
      <div
        className={`w-full h-full flex items-center justify-center bg-background-brandLight dark:bg-gray-800 border border-action-primary rounded-lg hover:border-solid ${isDragging ? "" : "border-dashed"}`}
      >
        {dropTitle ? (
          dropTitle
        ) : (
          <div className="flex flex-col items-center text-center">
            <DocRegularIcon color={APP_COLORS.content.disabled} width={20} height={20} />
            <p className="mt-2 font-medium text-content-primary dark:text-invertedPrimary">
              Drop document here
            </p>
            <p className="mt-0.5 text-xs text-content-secondary dark:text-invertedPrimary80">
              {formatsToLabel(DEAL_DOC_ACCEPT_FORMATS)} (max {MAX_FILE_SIZE_MB}MB)
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
